import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/axios";

type Summary = {
  date: string;
  totalTournees: number;
  totalPassages: number;
  completedPassages: number;
  pendingPassages: number;
  urgentPassages: number;
  latePassages: number;
  overloadedTournees: number;
  slaRiskTournees: number;
  averageProgress: number;
};

export default function TourneesAnalyticsPage(): React.JSX.Element {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(today);
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => {
    api.get("/tournees/analytics/summary", { params: { date } }).then((res) => {
      setSummary((res.data ?? null) as Summary | null);
    });
  }, [date]);

  return (
    <div className="space-y-5 p-4 text-slate-100 md:p-6">
      <header className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
        <h1 className="text-2xl font-bold text-white">Analyse tournées</h1>
        <p className="mt-1 text-sm text-slate-400">KPI analytiques journaliers pour pilotage opérationnel.</p>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-300">
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
          />
        </label>
      </header>
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile label="Total tournées" value={summary?.totalTournees ?? 0} />
        <Tile label="Passages réalisés" value={summary?.completedPassages ?? 0} />
        <Tile label="Passages en attente" value={summary?.pendingPassages ?? 0} />
        <Tile label="Urgences" value={summary?.urgentPassages ?? 0} />
        <Tile label="Retards" value={summary?.latePassages ?? 0} />
        <Tile label="Surchargées" value={summary?.overloadedTournees ?? 0} />
        <Tile label="SLA risk tournées" value={summary?.slaRiskTournees ?? 0} />
        <Tile label="Progression moyenne" value={`${summary?.averageProgress ?? 0}%`} />
      </section>
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <table className="min-w-full text-left text-sm">
          <tbody className="divide-y divide-slate-800">
            <Row label="Date" value={summary?.date ?? "—"} />
            <Row label="Total passages" value={String(summary?.totalPassages ?? 0)} />
            <Row label="Tournees surchargées" value={String(summary?.overloadedTournees ?? 0)} />
            <Row label="Tournées à risque SLA" value={String(summary?.slaRiskTournees ?? 0)} />
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string | number }) {
  return (
    <article className="rounded-2xl border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-white">{value}</p>
    </article>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td className="px-2 py-2 text-slate-400">{label}</td>
      <td className="px-2 py-2 text-white">{value}</td>
    </tr>
  );
}
